/**
 * IndexedDB access for the private health domain. See docs/11-data-model.md.
 *
 * v0.1 stores entries in the clear. That is a deliberate, temporary state:
 * at-rest encryption arrives in v0.4 with the PIN (ADR-0006), and the wrapped-
 * key design means enabling it will rewrap one key rather than re-encrypt the
 * data. The `id`-keyed layout below is already the encrypted-form layout — a
 * store keyed by '2026-08-14, 2026-08-15, ...' would leak a cycle history
 * without decrypting anything, so the opaque key is here from the first commit
 * rather than retrofitted.
 */
import type { DayEntry } from '@domain/types'
import type { SealedBlob } from './crypto'

export const DB_NAME = 'thana_v1'
export const DB_VERSION = 4

export const STORE = {
  entries: 'entries',
  settings: 'settings',
  symptoms: 'symptoms',
  customFields: 'customFields',
  meta: 'meta',
} as const

/**
 * The stored shape of an entry.
 *
 * `id` is an opaque random key and is the ONLY thing visible on disk. The date
 * lives inside the sealed body.
 *
 * Exactly one of `payload` (legacy plaintext, pre-v0.4) or `sealed` (ciphertext)
 * is present. Plaintext records are re-sealed lazily on read, per the
 * lazy-migration rule in data model Section 4.
 *
 * ## Why there is no date index any more
 *
 * Until v0.4 this carried a `dateIndex` field so the store could range-query by
 * month. That leaked WHICH days had an entry — not what any of them said, but a
 * pattern of logged dates is itself a cycle history, and the threat model rates
 * "the pattern of use" as critical (Section 1), not incidental.
 *
 * ADR-0006 always intended no date on disk. It could not happen in v0.4 because
 * `getEntry` and `getEntriesInRange` were index-backed. From v0.5 the store
 * builds an in-memory date-to-id map once per unlock and answers every query
 * from it, which is the decrypt-all-on-unlock model the ADR described. See
 * `entryStore.ts`.
 *
 * A stored record now reveals: how many days have entries, and roughly how large
 * each is. Nothing about when.
 */
export interface StoredEntry {
  id: string
  payload?: DayEntry
  sealed?: SealedBlob
}

let connection: Promise<IDBDatabase> | undefined

export function openDb(): Promise<IDBDatabase> {
  connection ??= new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onupgradeneeded = (event) => {
      const db = request.result
      const from = event.oldVersion

      /*
       * Migrations are forward-only and additive. Data model Section 4: a
       * migration never deletes; removing a store or field is a separate,
       * deliberate release.
       *
       * Each version gets its OWN block, and a new store goes in a NEW version
       * — never appended to an earlier block. That is not style: v0.1 shipped
       * with version 1, so an install that already exists never re-runs the
       * `from < 1` block. Adding the symptom store there meant every v0.1
       * install came up without it. Caught in a browser, on the first upgrade
       * this project ever performed, which is the argument for testing
       * migrations against a database created by the previous version rather
       * than a fresh one.
       */
      if (from < 1) {
        db.createObjectStore(STORE.entries, { keyPath: 'id' })
        db.createObjectStore(STORE.settings)
        db.createObjectStore(STORE.meta)
      }

      // v0.2 — symptom definitions (FR-204) and custom field definitions
      // (FR-212, populated from v1.1).
      if (from < 2) {
        const symptoms = db.createObjectStore(STORE.symptoms, { keyPath: 'id' })
        symptoms.createIndex('by-order', 'order')

        db.createObjectStore(STORE.customFields, { keyPath: 'id' })
      }

      /*
       * v0.4 — at-rest encryption (ADR-0006).
       *
       * No schema change is needed: `sealed` is a new optional field alongside
       * the existing `payload`, and existing plaintext records are re-sealed
       * lazily on read. Deliberately NOT a bulk re-encryption inside
       * `onupgradeneeded` — that runs before the user has unlocked anything, so
       * the key may not exist yet, and a half-finished pass over a health record
       * is the failure this design exists to avoid.
       *
       * The version is still bumped so the intent is recorded and a future
       * change has a numbered slot to live in.
       */
      if (from < 3) {
        // Intentionally empty. See above.
      }

      /*
       * v0.5 — remove every trace of dates from disk.
       *
       * Drops the `by-date` index and strips the `dateIndex` field from existing
       * records. This one CAN run in `onupgradeneeded`, unlike the v0.4
       * re-encryption: deleting a plaintext field needs no key, so there is no
       * dependency on the user having unlocked anything.
       *
       * Doing it here rather than lazily matters. A lazy strip would leave the
       * dates on disk until each record happened to be rewritten, which for an
       * old entry could be never — and "eventually" is not a property to offer
       * about a cycle history.
       */
      if (from < 4) {
        const entries = request.transaction?.objectStore(STORE.entries)
        if (entries) {
          if (entries.indexNames.contains('by-date')) entries.deleteIndex('by-date')
          if (entries.indexNames.contains('by-updated')) entries.deleteIndex('by-updated')

          const cursorRequest = entries.openCursor()
          cursorRequest.onsuccess = () => {
            const cursor = cursorRequest.result
            if (!cursor) return
            const record = cursor.value as StoredEntry & { dateIndex?: unknown }
            if (record.dateIndex !== undefined) {
              delete record.dateIndex
              cursor.update(record)
            }
            cursor.continue()
          }
        }
      }
    }

    request.onsuccess = () => {
      const db = request.result
      // A newer schema than this build understands: refuse clearly rather than
      // crash or silently lose data.
      db.onversionchange = () => db.close()
      resolve(db)
    }

    request.onerror = () => reject(request.error ?? new Error('Failed to open database'))
    request.onblocked = () =>
      reject(new Error('Database upgrade blocked by another open tab'))
  })

  return connection
}

/** Test seam. Not used by application code. */
export function resetConnectionForTests(): void {
  connection = undefined
}

/**
 * `IDBObjectStore.get` is typed `IDBRequest<any>` by the DOM lib, so the type
 * argument here is the one place the shape is asserted. Keeping it at the call
 * boundary means the rest of the module is honestly typed rather than sprinkled
 * with casts.
 */
export function promisify<T>(request: IDBRequest<T> | IDBRequest): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result as T)
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'))
  })
}

export function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error ?? new Error('Transaction failed'))
    tx.onabort = () => reject(tx.error ?? new Error('Transaction aborted'))
  })
}
